/**
 * The parts of an automation the Settings tab edits in place:
 *
 *   AutomationSentence   "When [an RFE or NOID response is coming due] within
 *                        [2 weeks], [write a plan first] and [ask before
 *                        making changes], using the [Quick model]." Each
 *                        bold part opens a small picker. A priority-date
 *                        automation picks its Visa Bulletin chart instead of
 *                        a window.
 *   ExpirationControls   which documents an expiration automation watches,
 *                        and whether dependents count
 *   DependentsSwitch     whether dependents' dates count
 *   WakeOverlapNote      the offer to turn off the assistant's own
 *                        expiration wake-up when both would act
 *   AutomationPreview    "Would catch": what an unsaved date automation
 *                        catches today, what it skips and why, and about how
 *                        many runs a month it starts
 */
import { Check, Loader2 } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LawFirmsAPI } from "@/api/lawFirms";
import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { cn } from "@/lib/utils";

import type {
  AutomationMatch,
  AutomationPreview as Preview,
  SkillAutonomy,
  SkillOptimizeFor,
  SkillOptions,
  SkillTriggerEdit
} from "@/features/firmKnowledge/types";
import {
  autonomyOf,
  chartOf,
  DATE_ACTIONS,
  daysLabel,
  describeSchedule,
  DOCUMENT_KIND_LABEL,
  EXPIRATION_ACTION,
  isDateAction,
  PRIORITY_ACTION,
  PRIORITY_CHART_LABEL,
  skillEvents
} from "./skillModel";

const MODEL_LABEL: Record<SkillOptimizeFor, string> = {
  cost: "Quick",
  balanced: "Balanced",
  intelligence: "Most capable"
};

const DEFAULT_PRESETS = [14, 30, 60, 90];

const DEFAULT_CHARTS = [
  { id: "final_action", label: "When a visa is available (Final Action)" },
  {
    id: "dates_for_filing",
    label: "When the client can start filing (Dates for Filing)"
  }
];

const lowerFirst = (text: string) =>
  text ? text.charAt(0).toLowerCase() + text.slice(1) : text;

/** "in 14 days", "today", "tomorrow". */
export const inDays = (days: number): string =>
  days <= 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;

// -- One editable part of the sentence ----------------------------------------

const SentencePart: FC<{
  label: string;
  testId: string;
  disabled?: boolean;
  children: (close: () => void) => ReactNode;
  className?: string;
}> = ({ label, testId, disabled, children, className }) => {
  const [open, setOpen] = useState(false);
  if (disabled) {
    return (
      <span className="font-medium" data-testid={testId}>
        {label}
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-sm px-0.5 font-medium underline decoration-fg-4 decoration-dotted underline-offset-4 hover:bg-fill-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={testId}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn("w-64 p-0", className)}>
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
};

type Choice<T extends string> = { value: T; label: string; hint?: string };

const ChoiceList = <T extends string>({
  choices,
  value,
  onPick,
  testIdPrefix
}: {
  choices: Choice<T>[];
  value: T;
  onPick: (value: T) => void;
  testIdPrefix: string;
}) => (
  <Command>
    <CommandList>
      <CommandGroup>
        {choices.map((c) => (
          <CommandItem
            key={c.value}
            value={c.label}
            onSelect={() => onPick(c.value)}
            data-testid={`${testIdPrefix}-${c.value}`}
          >
            <Check
              className={cn("size-3.5", c.value !== value && "opacity-0")}
            />
            <span className="flex flex-col">
              <span>{c.label}</span>
              {c.hint ? (
                <span className="text-sm text-fg-3">{c.hint}</span>
              ) : null}
            </span>
          </CommandItem>
        ))}
      </CommandGroup>
    </CommandList>
  </Command>
);

// -- The sentence -------------------------------------------------------------

/** The event a trigger switches to, with that action's defaults. */
export const withAction = (
  t: SkillTriggerEdit,
  action: string,
  defaultDays?: number | null
): Partial<SkillTriggerEdit> => {
  const date = isDateAction(action);
  return {
    event: {
      ...(t.event ?? {}),
      action,
      cooldown_hours: t.event?.cooldown_hours ?? 24,
      days_before: date ? defaultDays ?? DATE_ACTIONS[action] : null,
      kinds: action === EXPIRATION_ACTION ? t.event?.kinds ?? [] : [],
      include_dependents: t.event?.include_dependents ?? true,
      chart:
        action === PRIORITY_ACTION ? t.event?.chart ?? "final_action" : null
    },
    autonomy: date ? "propose" : t.autonomy ?? "act"
  };
};

export const AutomationSentence: FC<{
  trigger: SkillTriggerEdit;
  onChange: (patch: Partial<SkillTriggerEdit>) => void;
  options: SkillOptions | null;
  canEdit: boolean;
}> = ({ trigger: t, onChange, options, canEdit }) => {
  const events = skillEvents(options);
  const action = t.event?.action ?? "";
  const date = t.kind === "event" && isDateAction(action);
  const priority = date && action === PRIORITY_ACTION;
  const charts = options?.priority_charts?.length
    ? options.priority_charts
    : DEFAULT_CHARTS;
  const chart = chartOf(t);
  const eventLabel =
    events.find((e) => e.action === action)?.label ??
    (action || "something happens");
  const presets = options?.day_presets ?? DEFAULT_PRESETS;
  const days = t.event?.days_before ?? DATE_ACTIONS[action] ?? 14;
  const [custom, setCustom] = useState("");
  const models = options?.models?.length
    ? options.models
    : (Object.keys(MODEL_LABEL) as SkillOptimizeFor[]).map((id) => ({
        id,
        label: MODEL_LABEL[id]
      }));
  const model = t.optimize_for ?? "cost";
  const modelLabel =
    models.find((m) => m.id === model)?.label ?? MODEL_LABEL[model];
  const autonomy = autonomyOf(t);
  const mode = t.mode ?? "plan";

  return (
    <p className="text-sm leading-7" data-testid="automation-sentence">
      {t.kind === "schedule" ? (
        <span>{describeSchedule(t)}</span>
      ) : (
        <>
          When{" "}
          <SentencePart
            label={lowerFirst(eventLabel)}
            testId="skill-event-action"
            disabled={!canEdit}
            className="w-80"
          >
            {(close) => (
              <Command>
                <CommandList className="max-h-80">
                  {(["date", "activity"] as const).map((kind) => {
                    const group = events.filter(
                      (e) => (e.kind ?? "activity") === kind
                    );
                    if (!group.length) return null;
                    return (
                      <CommandGroup
                        key={kind}
                        heading={
                          kind === "date"
                            ? "As a date gets close or comes current"
                            : "When something happens on a case"
                        }
                      >
                        {group.map((e) => (
                          <CommandItem
                            key={e.action}
                            value={e.label}
                            onSelect={() => {
                              onChange(withAction(t, e.action, e.default_days));
                              close();
                            }}
                            data-testid={`skill-event-option-${e.action}`}
                          >
                            <Check
                              className={cn(
                                "size-3.5",
                                e.action !== action && "opacity-0"
                              )}
                            />
                            {e.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            )}
          </SentencePart>
          {priority ? (
            <>
              {" "}
              under{" "}
              <SentencePart
                label={`${PRIORITY_CHART_LABEL[chart] ?? "Final Action"} dates`}
                testId="skill-event-chart"
                disabled={!canEdit}
                className="w-80"
              >
                {(close) => (
                  <ChoiceList<string>
                    choices={charts.map((c) => ({
                      value: c.id,
                      label: c.label,
                      hint:
                        c.id === "dates_for_filing"
                          ? "Earlier; USCIS says each month whether adjustment filings may use it."
                          : "The visa can be issued, or the I-485 approved."
                    }))}
                    value={chart}
                    onPick={(v) => {
                      onChange({ event: { ...t.event, chart: v } });
                      close();
                    }}
                    testIdPrefix="skill-chart"
                  />
                )}
              </SentencePart>
            </>
          ) : date ? (
            <>
              {" "}
              within{" "}
              <SentencePart
                label={daysLabel(days)}
                testId="skill-event-days"
                disabled={!canEdit}
                className="w-72 p-3"
              >
                {(close) => (
                  <div className="space-y-3">
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={presets.includes(days) ? String(days) : ""}
                      onValueChange={(v) => {
                        if (!v) return;
                        onChange({
                          event: { ...t.event, days_before: Number(v) }
                        });
                        close();
                      }}
                      className="flex-wrap"
                    >
                      {presets.map((n) => (
                        <ToggleGroupItem
                          key={n}
                          value={String(n)}
                          data-testid={`skill-days-${n}`}
                        >
                          {daysLabel(n)}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    <form
                      className="flex items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const n = Math.round(Number(custom));
                        if (!n || n < 1 || n > 365) return;
                        onChange({ event: { ...t.event, days_before: n } });
                        setCustom("");
                        close();
                      }}
                    >
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        placeholder="Custom"
                        className="h-8"
                        data-testid="skill-days-custom"
                      />
                      <span className="text-sm text-fg-3">days</span>
                      <Button type="submit" size="sm" variant="secondary">
                        Set
                      </Button>
                    </form>
                  </div>
                )}
              </SentencePart>
            </>
          ) : null}
        </>
      )}
      ,{" "}
      <SentencePart
        label={
          mode === "plan" ? "write a plan first" : "start working right away"
        }
        testId="skill-trigger-mode"
        disabled={!canEdit}
        className="w-80"
      >
        {(close) => (
          <ChoiceList<"plan" | "agent">
            choices={[
              {
                value: "plan",
                label: "Write a plan first",
                hint: "It writes a plan and waits for approval before doing the work."
              },
              {
                value: "agent",
                label: "Start working right away",
                hint: "It starts on the work without waiting for a plan to be approved."
              }
            ]}
            value={mode}
            onPick={(v) => {
              onChange({ mode: v });
              close();
            }}
            testIdPrefix="skill-mode"
          />
        )}
      </SentencePart>{" "}
      and{" "}
      <SentencePart
        label={
          autonomy === "propose"
            ? "ask before making changes"
            : "make allowed changes on its own"
        }
        testId="skill-trigger-autonomy"
        disabled={!canEdit}
        className="w-80"
      >
        {(close) => (
          <ChoiceList<SkillAutonomy>
            choices={[
              {
                value: "propose",
                label: "Ask before making changes",
                hint: "Every change waits for approval. Best when no one is watching."
              },
              {
                value: "act",
                label: "Make allowed changes on its own",
                hint: "Changes this automation is allowed to make go ahead; the rest still ask."
              }
            ]}
            value={autonomy}
            onPick={(v) => {
              onChange({ autonomy: v });
              close();
            }}
            testIdPrefix="skill-autonomy"
          />
        )}
      </SentencePart>
      , using the{" "}
      <SentencePart
        label={`${modelLabel} model`}
        testId="skill-trigger-model"
        disabled={!canEdit}
        className="w-80"
      >
        {(close) => (
          <ChoiceList<SkillOptimizeFor>
            choices={models.map((m) => ({
              value: m.id,
              label: m.label,
              hint:
                m.id === "cost"
                  ? "Fast and inexpensive; right for most automations."
                  : undefined
            }))}
            value={model}
            onPick={(v) => {
              onChange({ optimize_for: v });
              close();
            }}
            testIdPrefix="skill-model"
          />
        )}
      </SentencePart>
      .
    </p>
  );
};

// -- Expirations ---------------------------------------------------------------

export const DependentsSwitch: FC<{
  trigger: SkillTriggerEdit;
  onChange: (patch: Partial<SkillTriggerEdit>) => void;
  canEdit: boolean;
  index: number;
}> = ({ trigger: t, onChange, canEdit, index }) => (
  <Label
    htmlFor={`skill-trigger-${index}-dependents`}
    className="flex items-center gap-2 text-sm font-normal"
  >
    <Switch
      id={`skill-trigger-${index}-dependents`}
      checked={t.event?.include_dependents !== false}
      onCheckedChange={(v) =>
        onChange({ event: { ...t.event, include_dependents: v } })
      }
      disabled={!canEdit}
      data-testid="skill-event-dependents"
    />
    Include the client's dependents
  </Label>
);

export const ExpirationControls: FC<{
  trigger: SkillTriggerEdit;
  onChange: (patch: Partial<SkillTriggerEdit>) => void;
  options: SkillOptions | null;
  canEdit: boolean;
  index: number;
}> = ({ trigger: t, onChange, options, canEdit, index }) => {
  const kinds =
    options?.document_kinds ??
    Object.entries(DOCUMENT_KIND_LABEL).map(([id, label]) => ({ id, label }));
  const picked = t.event?.kinds ?? [];
  return (
    <div className="space-y-2" data-testid="skill-expiration-controls">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-fg-3">Documents</span>
        <ToggleGroup
          type="multiple"
          variant="outline"
          size="sm"
          value={picked}
          onValueChange={(v) => onChange({ event: { ...t.event, kinds: v } })}
          disabled={!canEdit}
          className="flex-wrap"
        >
          {kinds.map((k) => (
            <ToggleGroupItem
              key={k.id}
              value={k.id}
              data-testid={`skill-kind-${k.id}`}
            >
              {k.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {picked.length === 0 ? (
          <span className="text-sm text-fg-3">
            None picked: every document.
          </span>
        ) : null}
      </div>
      <DependentsSwitch
        trigger={t}
        onChange={onChange}
        canEdit={canEdit}
        index={index}
      />
    </div>
  );
};

/** Shown when the firm's assistant also wakes up for expirations. */
export const WakeOverlapNote: FC<{ lawFirmId: string; canEdit: boolean }> = ({
  lawFirmId,
  canEdit
}) => {
  const [state, setState] = useState<"shown" | "saving" | "done">("shown");
  if (state === "done") return null;
  const turnOff = async () => {
    setState("saving");
    try {
      const firm = await LawFirmsAPI.getById(lawFirmId);
      await LawFirmsAPI.update(lawFirmId, {
        agent_event_wakes: {
          ...(firm?.agent_event_wakes ?? {}),
          expiration: false
        }
      });
      toast.success("The assistant no longer wakes up for expirations.");
      setState("done");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Could not change the firm's setting."
      );
      setState("shown");
    }
  };
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warn-line bg-warn-soft px-3 py-2 text-sm text-warn"
      data-testid="automation-wake-note"
    >
      <span>
        The assistant also wakes up for expiring documents, so both may act on
        the same date.
      </span>
      {canEdit ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={state === "saving"}
          onClick={() => void turnOff()}
          data-testid="automation-wake-off"
        >
          Turn off the assistant's expiration wake-up
        </Button>
      ) : null}
    </div>
  );
};

// -- Would catch ---------------------------------------------------------------

const PREVIEW_DELAY_MS = 500;
const SHOWN = 5;

/** The fields that change what a date automation catches. */
const previewKey = (t: SkillTriggerEdit) =>
  JSON.stringify({
    id: t.id,
    event: t.event
      ? {
          action: t.event.action,
          days_before: t.event.days_before ?? null,
          kinds: t.event.kinds ?? [],
          include_dependents: t.event.include_dependents ?? true,
          chart: t.event.chart ?? null
        }
      : null,
    case_filter: t.case_filter ?? null
  });

/** "Luis Ruiz's Passport in 12 days"; "EB-2 priority date is current". */
export const matchPhrase = (m: AutomationMatch, priority: boolean): string => {
  const first = m.items[0];
  if (m.items.length > 1) {
    return priority
      ? `${m.items.length} priority dates are current`
      : `${m.items.length} items, the nearest ${inDays(m.days_left)}`;
  }
  const who = first?.who && first.who !== m.client ? `${first.who}'s ` : "";
  return priority
    ? `${who}${first?.label ?? "Priority date"} is current`
    : `${who}${first?.label ?? "Item"} ${inDays(m.days_left)}`;
};

export const AutomationPreview: FC<{
  skillId: string;
  trigger: SkillTriggerEdit;
  scope: FirmScope;
}> = ({ skillId, trigger, scope }) => {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const key = previewKey(trigger);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const payload: SkillTriggerEdit = { kind: "event", ...JSON.parse(key) };
    const timer = window.setTimeout(() => {
      firmKnowledgeApi
        .previewAutomation(skillId, payload, scope)
        .then((p) => {
          if (cancelled) return;
          setPreview(p);
          setFailed(false);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [skillId, key, scope]);

  const matches = preview?.matches ?? [];
  const skipped = preview?.skipped ?? [];
  const priority = trigger.event?.action === PRIORITY_ACTION;
  return (
    <div
      className="space-y-2 rounded-lg bg-fill-1 p-3"
      data-testid="automation-preview"
    >
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium">Would catch today</span>
        {loading ? (
          <Loader2 className="size-3.5 animate-spin text-fg-3" />
        ) : preview ? (
          <span className="text-fg-3" data-testid="automation-preview-estimate">
            {priority
              ? "Checks each new Visa Bulletin"
              : preview.runs_per_month
              ? `About ${preview.runs_per_month} ${
                  preview.runs_per_month === 1 ? "run" : "runs"
                } a month`
              : matches.length
              ? "Few new runs after these"
              : "Rarely starts a run"}
          </span>
        ) : null}
      </div>
      {failed ? (
        <p className="text-sm text-fg-3">
          Could not check the firm's cases right now.
        </p>
      ) : !loading && preview && matches.length === 0 ? (
        <p className="text-sm text-fg-3">
          {priority
            ? "Nothing today. It starts when a Visa Bulletin makes a client's priority date current."
            : `Nothing today. It starts when a date comes within ${daysLabel(
                preview.window_days ?? 0
              )}.`}
        </p>
      ) : null}
      {matches.length ? (
        <ul className="space-y-1 text-sm">
          {matches.slice(0, SHOWN).map((m) => (
            <li
              key={`${m.individual_id}:${m.items[0]?.item_key ?? ""}`}
              className="flex items-center gap-2"
              data-testid="automation-preview-match"
            >
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium">{m.client ?? "A client"}</span>
                {m.case_label ? (
                  <span className="text-fg-3"> · {m.case_label}</span>
                ) : null}
                <span> · {matchPhrase(m, priority)}</span>
              </span>
              {m.state !== "new" ? (
                <StatusBadge>
                  {m.state === "started" ? "Already Started" : "Skipped"}
                </StatusBadge>
              ) : null}
            </li>
          ))}
          {matches.length > SHOWN ? (
            <li className="text-fg-3">and {matches.length - SHOWN} more</li>
          ) : null}
        </ul>
      ) : null}
      {skipped.length ? (
        <div className="space-y-1 border-t border-line-2 pt-2 text-sm">
          <span className="font-medium text-fg-3">
            Skipped: already handled
          </span>
          <ul className="space-y-1" data-testid="automation-preview-skipped">
            {skipped.slice(0, SHOWN).map((s) => (
              <li key={`${s.individual_id}:${s.item_key}`}>
                <span className="font-medium">{s.client ?? "A client"}</span> ·{" "}
                {s.label}: <span className="text-fg-3">{s.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
